import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal
} from "@angular/core";
import { round } from "es-toolkit";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import { pickFile, pickFiles } from "src/app/support/file";
import { UploadProgressInfo } from "src/app/support/upload-progress";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("DropzoneComponent");
export type DropzoneEntry = {
  file?: File;
  name: string;
  size?: number;
};

enum DropzoneUploadStatus {
  STAND_BY = "stand-by",
  IN_PROGRESS = "in-progress",
  DONE = "done",
  ERROR = "error"
}

@Component({
  selector: "snt-dropzone",
  templateUrl: "dropzone.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  styles: `
    :host {
      display: flex;
      flex: 1;
    }
  `
})
export class DropzoneComponent {
  label = input<string | undefined>(undefined);
  icon = input<string | undefined>(undefined);

  acceptedFileTypes = input<string[] | undefined>(undefined);
  allowMultiple = input<boolean>(false);
  maxFileSize = input<number | undefined>(undefined);
  maxTotalFileSize = input<number | undefined>(undefined);
  height = input<string | undefined>(undefined);

  disableUpload = input<boolean>(false);

  hideProgressBar = input<boolean>(false);

  filesChanghed = output<DropzoneEntry[]>();

  files = signal<DropzoneEntry[] | undefined>(undefined);

  status = signal<DropzoneUploadStatus>(DropzoneUploadStatus.STAND_BY);

  size = input<"default" | "xl">("default");
  centerContent = input<boolean>(false);

  progressMap = signal<Record<number, number>>({});
  progressStats = signal<
    Record<
      number,
      {
        completed: boolean;
        loaded: number;
        total: number;
        speed: number;
      }
    >
  >({});

  protected readonly DropzoneUploadStatus = DropzoneUploadStatus;

  readonly #storageBackend = inject(StorageBackend);

  constructor() {
    effect(() => {
      const files = this.files();
      if (files) {
        this.filesChanghed.emit(files);
      }

      this.progressStats.update((value) => {
        files?.forEach((file, index) => {
          value[index] = {
            completed: false,
            total: file.size || 0,
            loaded: 0,
            speed: 0
          };
        });

        return { ...value };
      });
    });
  }

  onDragOver(dragOverEvent: DragEvent): void {
    dragOverEvent.preventDefault();
    dragOverEvent.stopPropagation();
  }

  onDrop(dropEvent: DragEvent): void {
    dropEvent.stopImmediatePropagation();
    dropEvent.preventDefault();

    const file = dropEvent.dataTransfer?.files?.item(0);

    if (!file) {
      return;
    }

    this.files.set([fileToDropzoneEntry(file)]);
  }

  passFileByExt(file: File): void {
    this.files.set([fileToDropzoneEntry(file)]);
  }

  async onClick(): Promise<void> {
    let files: File[] | null = null;

    if (this.allowMultiple()) {
      files = await pickFiles({
        acceptedFileTypes: this.acceptedFileTypes() || undefined
      });
    } else {
      files = await pickFile({
        acceptedFileTypes: this.acceptedFileTypes() || undefined
      }).then((file) => (file ? [file] : null));
    }

    if (files) {
      this.files.set(files.map(fileToDropzoneEntry));
    }
  }

  onRemove(index: number): void {
    const currentFiles = this.files();
    if (currentFiles) {
      let updatedFiles = [...currentFiles];
      updatedFiles.splice(index, 1);
      this.files.set(updatedFiles);
    }
  }

  async upload(): Promise<string[]> {
    this.status.set(DropzoneUploadStatus.IN_PROGRESS);

    const currentFiles = this.files();
    if (!currentFiles || currentFiles.length === 0) {
      this.status.set(DropzoneUploadStatus.DONE);
      return [];
    }

    try {
      const storageIds = await Promise.all(
        currentFiles.map((entry: DropzoneEntry, index: number) =>
          this.uploadSingleFile(entry, index)
        )
      );

      return storageIds.filter((id): id is string => id !== null);
    } catch (ex) {
      this.status.set(DropzoneUploadStatus.ERROR);
      throw ex;
    }
  }

  private async uploadSingleFile(
    entry: DropzoneEntry,
    index: number
  ): Promise<string | null> {
    if (!entry.file) {
      return null;
    }

    const { storageId } = await this.#storageBackend.uploadV2({
      body: entry.file,
      name: entry.file.name,
      storageType: "default",
      onProgress: (progress: UploadProgressInfo) => {
        this.progressMap.update((value) => {
          const progressPercentage = round(
            (progress.loaded / progress.total) * 100,
            2
          );
          logger.debug(
            `Progress: ${progressPercentage}% - Speed: ${this.formatSpeed(progress.speed)}`
          );
          value[index] = progressPercentage;
          return { ...value };
        });

        this.progressStats.update((value) => {
          value[index] = {
            completed: false,
            total: value[index].total,
            loaded: progress.loaded,
            speed: progress.speed
          };

          return { ...value };
        });
      }
    });

    this.progressMap.update((value) => {
      value[index] = 100;
      return value;
    });

    this.progressStats.update((value) => {
      value[index] = {
        completed: true,
        total: value[index].total,
        loaded: value[index].total,
        speed: 0
      };

      return { ...value };
    });

    return storageId;
  }

  private formatSpeed(bytesPerSecond: number): string {
    if (bytesPerSecond < 1024) {
      return `${Math.round(bytesPerSecond)} B/s`;
    } else if (bytesPerSecond < 1024 * 1024) {
      return `${Math.round(bytesPerSecond / 1024)} KB/s`;
    } else {
      return `${Math.round(bytesPerSecond / (1024 * 1024))} MB/s`;
    }
  }
}

function fileToDropzoneEntry(file: File): DropzoneEntry {
  return {
    name: file.name,
    size: file.size,
    file
  };
}
